import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Star,
  Loader2,
  Flame,
  Sparkles,
  Camera,
  ChevronUp,
  AlertCircle,
  WifiOff,
  Check,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/stores/authStore';
import { CelebrationOrchestrator } from '@/components/celebrations';
import { queueScan, getQueueCount } from '@/lib/offlineQueue';
import PushOptInPrompt, { incrementScanCount, shouldShowPushPrompt } from '@/components/PushOptInPrompt';
import PhotoCapture from '@/components/PhotoCapture';
import { StatusGrid, StatusUpdateModal } from '@/components/status';
import type { StatusItem } from '@/components/status';
import { getAmenityIcon } from '@tendedloop/shared/react';
import { SCOUT_POINT_VALUES } from '@tendedloop/shared';
import { rankName } from '@/utils/rankName';
import { catalogText } from '@/utils/catalog';
import { apiErrorText } from '@tendedloop/shared/i18n';

// ============================================
// TYPES
// ============================================

interface ScanResult {
  scanId: string;
  feedbackId: string | null;
  xpAwarded: number;
  xpBreakdown: Array<{ type: string; amount: number }>;
  streak: { current: number; isFirstOfDay: boolean };
  levelUp: {
    leveledUp: boolean;
    previousLevel: number;
    newLevel: number;
    levelName: string;
  };
  amenityName: string;
  buildingName: string;
  completedMissions: Array<{ id: string; slug?: string; name: string; xpReward: number }>;
  newBadges: Array<{
    id: string;
    slug: string;
    name: string;
    description: string | null;
    rarity: string;
    category: string;
    xpReward: number;
  }>;
  newLogo?: { tenantName: string; logoUrl: string | null };
  animalDiscovery?: {
    discovered: boolean;
    restored: boolean;
    animalSlug: string;
    animalName: string;
    fieldNote: string;
    imagePath: string;
    thumbPath: string;
    habitat: string;
    milestone?: number;
  };
}

interface AmenityInfo {
  amenity: {
    id: string;
    name: string;
    amenityType: { name: string; icon: string | null };
  };
  building: { name: string };
  location: { name: string };
  statusItems: StatusItem[];
}

// ============================================
// COMPONENT
// ============================================

export default function FeedbackFlow() {
  const { t, i18n } = useTranslation('scout');
  const { qrCode } = useParams<{ qrCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, scout } = useAuthStore();

  // Amenity loading
  const [amenityInfo, setAmenityInfo] = useState<AmenityInfo | null>(null);
  const [amenityLoading, setAmenityLoading] = useState(true);
  const [amenityError, setAmenityError] = useState<string | null>(null);

  // Feedback form
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [capturedImage, setCapturedImage] = useState<File | null>(null);

  // Status update modal
  const [selectedStatusItem, setSelectedStatusItem] = useState<StatusItem | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusToast, setStatusToast] = useState<string | null>(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);

  // XP animation
  const [showXpFloat, setShowXpFloat] = useState(false);
  const [isFirstEverScan, setIsFirstEverScan] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Offline
  const [offlineQueued, setOfflineQueued] = useState(false);
  const [offlinePending, setOfflinePending] = useState(0);

  // Push opt-in
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated) {
      if (qrCode) {
        localStorage.setItem('scout_pendingQrCode', qrCode);
      }
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate, qrCode]);

  // Load amenity info from QR code
  useEffect(() => {
    if (!qrCode) return;
    setAmenityLoading(true);
    setAmenityError(null);

    fetch(`${(import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/status/${qrCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          setAmenityInfo(data.data);
        } else {
          setAmenityError(apiErrorText(i18n, data, t('feedback.loadError')));
        }
      })
      .catch(() => setAmenityError(t('feedback.loadError')))
      .finally(() => setAmenityLoading(false));
  }, [qrCode, t]);

  // Auto-submit when arriving from visitor CTA (feedback already submitted anonymously)
  const [autoSubmitting, setAutoSubmitting] = useState(() => !!localStorage.getItem('scout_autoSubmit'));

  useEffect(() => {
    if (!autoSubmitting || !isAuthenticated || !qrCode || amenityLoading || amenityError) return;

    localStorage.removeItem('scout_autoSubmit');
    handleSubmit({ skipFeedback: true });
  }, [autoSubmitting, isAuthenticated, qrCode, amenityLoading, amenityError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss status toast
  useEffect(() => {
    if (!statusToast) return;
    const timer = setTimeout(() => setStatusToast(null), 3000);
    return () => clearTimeout(timer);
  }, [statusToast]);

  // Handle status item click
  const handleStatusItemClick = (item: StatusItem) => {
    setSelectedStatusItem(item);
    setShowStatusModal(true);
  };

  // Submit status update via public endpoint
  const handleStatusUpdate = async (statusItemId: string, newStatus: string, statusComment?: string) => {
    if (!qrCode) return;

    const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
    const res = await fetch(`${baseUrl}/api/status/${qrCode}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        statusItemId,
        newStatus,
        comment: statusComment,
      }),
    });

    const data = await res.json();
    if (!data.success) {
      throw new Error(apiErrorText(i18n, data, t('status.updateError')));
    }

    // Update local state with new status
    setAmenityInfo((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        statusItems: prev.statusItems.map((item) =>
          item.id === statusItemId
            ? { ...item, currentStatus: newStatus, lastUpdatedAt: new Date().toISOString() }
            : item,
        ),
      };
    });

    // Haptic + success toast for status update confirmation
    navigator.vibrate?.(50);
    setStatusToast(t('status.updated'));
  };

  // Submit scan
  const handleSubmit = async (options?: { skipFeedback?: boolean }) => {
    if (!qrCode || submitting) return;
    navigator.vibrate?.(50);

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Upload photo if captured
      let imageKey: string | undefined;
      let imageMimeType: string | undefined;
      let imageFileSize: number | undefined;

      if (capturedImage && !options?.skipFeedback) {
        try {
          const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
          const uploadUrlRes = await fetch(`${baseUrl}/api/feedback/upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              qrCode,
              mimeType: capturedImage.type || 'image/jpeg',
              fileSize: capturedImage.size,
            }),
          });
          const uploadUrlData = await uploadUrlRes.json();

          if (
            uploadUrlRes.ok &&
            uploadUrlData.success &&
            uploadUrlData.data?.uploadUrl &&
            uploadUrlData.data?.imageKey
          ) {
            const { uploadUrl, imageKey: key } = uploadUrlData.data;
            const uploadMimeType = capturedImage.type || 'image/jpeg';

            const uploadRes = await fetch(uploadUrl, {
              method: 'PUT',
              body: capturedImage,
              headers: { 'Content-Type': uploadMimeType },
            });

            if (uploadRes.ok) {
              imageKey = key;
              imageMimeType = uploadMimeType;
              imageFileSize = capturedImage.size;
            }
          }
        } catch {
          // Continue without photo - upload failure is non-critical
        }
      }

      const scanResult = await api.post<ScanResult>('/scout/scan', {
        qrCode,
        rating: rating > 0 ? rating : undefined,
        comment: comment.trim() || undefined,
        imageKey,
        imageMimeType,
        imageFileSize,
        skipFeedback: options?.skipFeedback,
      });

      setResult(scanResult);

      // Track in GA4
      window.gtag?.('event', 'scout_scan', {
        xp_awarded: scanResult.xpAwarded,
        has_rating: rating > 0,
        has_comment: !!comment.trim(),
        has_photo: !!imageKey,
      });

      // Trigger XP float animation
      setTimeout(() => setShowXpFloat(true), 300);

      // Mark first scan done and track count for push opt-in
      if (!localStorage.getItem('scout_first_scan_done')) {
        setIsFirstEverScan(true);
      }
      localStorage.setItem('scout_first_scan_done', '1');
      incrementScanCount();
      if (shouldShowPushPrompt()) {
        setTimeout(() => setShowPushPrompt(true), 2000);
      }

      // Refresh profile in auth store
      useAuthStore.getState().checkAuth();
    } catch (err) {
      // Queue offline if network error
      const isNetworkError = !navigator.onLine || err instanceof TypeError;
      if (isNetworkError) {
        await queueScan({
          qrCode,
          rating: rating > 0 ? rating : undefined,
          comment: comment.trim() || undefined,
        });
        const count = await getQueueCount();
        setOfflinePending(count);
        setOfflineQueued(true);
      } else {
        setSubmitError(err instanceof Error ? err.message : t('feedback.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // LOADING STATE
  // ============================================

  if (amenityLoading || (autoSubmitting && !result && !submitError)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
        <p className="text-small text-muted-foreground">{t('feedback.loading')}</p>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================

  if (amenityError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white flex flex-col items-center justify-center px-6">
        <div className="w-16 h-16 rounded-full bg-critical/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-critical" />
        </div>
        <p className="text-small text-muted-foreground text-center mb-4">{amenityError}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2.5 rounded-xl bg-muted text-small font-medium text-foreground"
        >
          {t('feedback.retry')}
        </button>
      </div>
    );
  }

  // ============================================
  // OFFLINE QUEUED STATE
  // ============================================

  if (offlineQueued) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-warm-faint to-white flex flex-col items-center justify-center px-6 pb-safe">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4 animate-scale-in">
          <WifiOff className="w-10 h-10 text-steel" />
        </div>
        <h2 className="text-section font-bold text-foreground mb-1">{t('offline.savedOffline')}</h2>
        <p className="text-small text-muted-foreground text-center mb-6">{t('offline.willSyncLater')}</p>
        {offlinePending > 0 && (
          <p className="text-caption text-muted-foreground mb-6">
            {t('offline.pendingScans', { count: offlinePending })}
          </p>
        )}
        <div className="w-full flex flex-col gap-3 max-w-xs">
          <button
            onClick={() => navigate('/scan')}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-95 transition-all"
          >
            <Camera className="w-5 h-5" />
            {t('feedback.scanAnother')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 rounded-xl bg-muted text-foreground font-medium text-small active:scale-95 transition-all"
          >
            {t('feedback.backHome')}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // SUCCESS STATE. XP Award Result
  // ============================================

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-safe">
        <CelebrationOrchestrator result={result} />
        {showPushPrompt && <PushOptInPrompt onClose={() => setShowPushPrompt(false)} />}
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-full hover:bg-warm-faint"
            aria-label={t('common.goBack')}
          >
            <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <h1 className="text-section font-semibold text-foreground">{result.amenityName}</h1>
        </div>

        <div className="flex flex-col items-center px-6 pt-6">
          {/* First scan welcome or XP animation */}
          {isFirstEverScan ? (
            <div className="text-center mb-4 animate-scale-in">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gold to-gold/70 flex items-center justify-center shadow-lg mx-auto mb-3">
                <span className="text-3xl" role="img" aria-hidden="true">
                  🎉
                </span>
              </div>
              <h2 className="text-xl font-bold text-foreground mb-1">{t('feedback.firstScanTitle')}</h2>
              <p className="text-small text-muted-foreground">{t('feedback.firstScanMessage')}</p>
              <div className="mt-3">
                <span className="text-2xl font-black text-forest">
                  {t('feedback.xpEarned', { xp: result.xpAwarded })}
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-forest flex items-center justify-center shadow-lg animate-scale-in">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                {showXpFloat && (
                  <div className="absolute -top-4 start-1/2 -translate-x-1/2 animate-float-up">
                    <span className="text-2xl font-black text-forest">
                      {t('feedback.xpEarned', { xp: result.xpAwarded })}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          <p className="text-small text-muted-foreground mt-6 mb-1">{result.buildingName}</p>
          {/* Globe: "Trail recorded" for healthy returning scans */}
          {scout?.globeEnabled !== false && !result.animalDiscovery && (
            <p className="text-caption text-forest font-medium mb-1">
              {t('globe:scan.trailRecorded', { defaultValue: 'Trail recorded.' })}
            </p>
          )}
          <p className="text-caption text-muted-foreground">
            {t('feedback.progressSummary', {
              level: result.levelUp.newLevel,
              name: rankName(result.levelUp.newLevel, result.levelUp.levelName),
            })}
          </p>

          {/* Streak & First of Day badges */}
          <div className="flex items-center gap-2 mt-2 mb-4">
            {result.streak.isFirstOfDay && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gold/10 text-gold text-caption font-medium">
                <Flame className="w-3.5 h-3.5" />
                {t('feedback.firstOfDay')}
              </span>
            )}
            {result.streak.current > 1 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gold/10 text-gold text-caption font-medium">
                <Flame className="w-3.5 h-3.5" />
                {t('feedback.streakDay', { count: result.streak.current })}
              </span>
            )}
          </div>

          {/* XP Breakdown */}
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center gap-1 text-small text-muted-foreground hover:text-foreground mb-2"
          >
            {t('feedback.xpBreakdownTitle')}
            <ChevronUp className={`w-4 h-4 transition-transform ${showBreakdown ? '' : 'rotate-180'}`} />
          </button>

          {showBreakdown && (
            <div className="w-full bg-white rounded-xl border border-warm-border shadow-sm p-3 mb-4 animate-fade-in">
              {result.xpBreakdown.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between py-1.5 border-b border-warm-faint last:border-0"
                >
                  <span className="text-small text-muted-foreground">
                    {t(`feedback.xpType.${item.type}`, { defaultValue: item.type })}
                  </span>
                  <span className="text-small font-semibold text-forest">+{item.amount}</span>
                </div>
              ))}
            </div>
          )}

          {/* What's Next - show completed missions ready to claim */}
          {result.completedMissions.length > 0 && (
            <div className="w-full mt-4 animate-fade-in">
              <button
                onClick={() => navigate('/missions')}
                className="w-full p-3 rounded-xl bg-gold/5 border border-gold/20 flex items-center gap-3 active:scale-[0.98] transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1 text-start min-w-0">
                  <p className="text-caption text-gold font-medium">{t('feedback.missionReady')}</p>
                  <p className="text-small font-semibold text-foreground truncate">
                    {catalogText(result.completedMissions[0]!.slug, 'name', result.completedMissions[0]!.name)}
                  </p>
                </div>
                <span className="text-caption font-bold text-forest">+{result.completedMissions[0]!.xpReward}</span>
              </button>
            </div>
          )}

          {/* Level up hint for first-time users */}
          {result.levelUp.leveledUp && (
            <div className="w-full mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20 text-center animate-fade-in">
              <p className="text-small font-semibold text-foreground">
                {t('feedback.levelUpMessage', {
                  level: result.levelUp.newLevel,
                  name: rankName(result.levelUp.newLevel, result.levelUp.levelName),
                })}
              </p>
            </div>
          )}

          {/* Streak teaser for first scan */}
          {isFirstEverScan && (
            <div className="w-full mt-4 p-3 rounded-xl bg-gold/5 border border-gold/20 text-center animate-fade-in">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Flame className="w-4 h-4 text-gold" />
                <p className="text-small font-semibold text-foreground">{t('feedback.streakTeaser')}</p>
              </div>
              <p className="text-caption text-muted-foreground">{t('feedback.streakTeaserHint')}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="w-full flex flex-col gap-3 mt-4">
            <button
              onClick={() => navigate('/scan')}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-95 transition-all"
            >
              <Camera className="w-5 h-5" />
              {t('feedback.scanAnother')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-xl bg-muted text-foreground font-medium text-small active:scale-95 transition-all"
            >
              {t('feedback.backHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // FEEDBACK FORM
  // ============================================

  const amenityName = amenityInfo?.amenity.name || qrCode || '';
  const amenityTypeName = amenityInfo?.amenity.amenityType.name || 'default';
  const buildingName = amenityInfo?.building.name || '';
  const statusItems = amenityInfo?.statusItems || [];
  const displayRating = hoverRating || rating;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-safe">
      {/* Status update toast */}
      {statusToast && (
        <div
          className="fixed top-4 inset-x-4 z-40 flex justify-center animate-fade-in"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-forest text-white shadow-lg text-small font-medium">
            <Check className="w-4 h-4" />
            {statusToast}
          </div>
        </div>
      )}

      {/* Status update modal */}
      <StatusUpdateModal
        open={showStatusModal}
        onOpenChange={setShowStatusModal}
        item={selectedStatusItem}
        onSubmit={handleStatusUpdate}
      />

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-warm-faint"
          aria-label={t('common.goBack')}
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-semibold text-foreground">{t('feedback.title')}</h1>
          {buildingName && <p className="text-caption text-muted-foreground">{buildingName}</p>}
        </div>
      </div>

      {/* Amenity Info */}
      <div className="mx-4 p-4 rounded-2xl bg-white shadow-sm border border-warm-border mb-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center">{getAmenityIcon(amenityTypeName, '#334155', 28)}</span>
          <div>
            <p className="font-semibold text-foreground">{amenityName}</p>
            {amenityInfo?.location && <p className="text-caption text-muted-foreground">{amenityInfo.location.name}</p>}
          </div>
        </div>
        <p className="text-caption text-primary/70 mt-3 pt-3 border-t border-warm-border">{t('feedback.impactLine')}</p>
      </div>

      {/* Status Items Grid */}
      {statusItems.length > 0 && (
        <div className="px-4 mb-6">
          <h2 className="text-small font-semibold text-foreground mb-3">{t('status.tapToUpdate')}</h2>
          <StatusGrid statusItems={statusItems} onItemClick={handleStatusItemClick} />
        </div>
      )}

      {/* Rating */}
      <div className="px-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-small font-semibold text-foreground">
            {t('feedback.rateTitle', {
              // "How was this washroom?" reads naturally lowercased in English; other languages keep the name as written.
              amenity: i18n.language?.startsWith('en') ? amenityName.toLowerCase() : amenityName,
            })}
          </h2>
          {rating === 0 && (
            <span className="text-caption font-medium text-gold">
              {t('feedback.xpEarned', { xp: SCOUT_POINT_VALUES.FEEDBACK })}
            </span>
          )}
        </div>

        {/* Star Rating */}
        <div className="flex items-center justify-center gap-2 py-3">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => {
                navigator.vibrate?.(50);
                setRating(star === rating ? 0 : star);
              }}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onTouchStart={() => setHoverRating(star)}
              onTouchEnd={() => {
                setHoverRating(0);
                setRating(star === rating ? 0 : star);
              }}
              className="p-1.5 transition-transform active:scale-110"
              aria-label={t('feedback.starRating', { count: star })}
            >
              <Star
                className={`w-10 h-10 transition-colors ${
                  star <= displayRating ? 'text-gold fill-gold' : 'text-warm-border'
                }`}
              />
            </button>
          ))}
        </div>
        <p className="text-caption text-muted-foreground text-center">{t('feedback.rateHint')}</p>
      </div>

      {/* Comment */}
      <div className="px-4 mb-6">
        <label className="block text-small font-semibold text-foreground mb-2">{t('feedback.comment')}</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('feedback.commentPlaceholder')}
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl border border-warm-border bg-white text-small focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
        {comment.length > 0 && (
          <p className={`text-end text-caption mt-1 ${comment.length > 450 ? 'text-gold' : 'text-muted-foreground'}`}>
            {comment.length}/500
          </p>
        )}
      </div>

      {/* Photo */}
      <div className="px-4 mb-6">
        <PhotoCapture
          onCapture={setCapturedImage}
          capturedImage={capturedImage}
          onRemove={() => setCapturedImage(null)}
          disabled={submitting}
        />
      </div>

      {/* Submit Error */}
      {submitError && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-critical/5 border border-critical/10">
          <p className="text-small text-critical">{submitError}</p>
        </div>
      )}

      {/* Submit Button */}
      <div className="px-4">
        <button
          onClick={() => handleSubmit()}
          disabled={submitting}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('feedback.submitting')}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              {t('feedback.submit')}
            </>
          )}
        </button>
        <p className="text-caption text-muted-foreground text-center mt-2" aria-live="polite">
          {t('feedback.xpPreview', {
            xp:
              SCOUT_POINT_VALUES.SCAN +
              (rating > 0 ? SCOUT_POINT_VALUES.FEEDBACK : 0) +
              (capturedImage ? SCOUT_POINT_VALUES.PHOTO : 0),
          })}
        </p>
      </div>
    </div>
  );
}
